import { ILabelStyleProps, ILabelStyles } from '@fluentui/react/lib/Label';
import {
  ITextFieldStyleProps,
  ITextFieldStyles
} from '@fluentui/react/lib/TextField';
import { ITheme } from '@fluentui/react/lib/Styling';
import { ContrastColors, CoreColors, DarkColors } from '../teams-colors';
import { FontSizes } from '../teams-type';
import { teamsBorderRadius, teamsButtonBorderRadius } from './constants';

function getLabelStyles(props: ILabelStyleProps): ILabelStyles {
  return {
    root: {
      color: props.theme.palette.neutralDark,
      paddingTop: 0,
      paddingBottom: '6px',
      fontSize: FontSizes.size12
    }
  };
}

export function getTextFieldStyles(
  themeName: string,
  _theme: ITheme,
  background?: string
) {
  return (props: ITextFieldStyleProps): Partial<ITextFieldStyles> => {
    const { focused, disabled, hasErrorMessage, multiline, theme } = props;
    const { palette } = theme;
    const FIELD_HEIGHT = '32px';
    const MULTILINE_FIELD_HEIGHT = '94px';

    let FIELD_GROUP_BACKGROUND_COLOR = palette.neutralLight;
    let FIELD_GROUP_OPACITY = 0.36;
    let FIELD_COLOR = palette.black;
    let FIELD_COLOR_PLACEHOLDER_NOT_DISABLED = palette.neutralDark;
    let FIELD_COLOR_PLACEHOLDER_DISABLED = palette.neutralDark;
    let FIELD_GROUP_BORDER_WIDTH = '0 0 2px 0';
    let FIELD_GROUP_BORDER_COLOR = 'transparent';
    let FIELD_GROUP_BORDER_COLOR_DISABLED = 'transparent';
    let FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE = palette.themePrimary;
    let FIELD_GROUP_BORDER_WIDTH_ACTIVE = FIELD_GROUP_BORDER_WIDTH;
    let ERROR_MESSAGE_COLOR = CoreColors.appRed;

    // Dark theme overrides
    if (themeName === 'dark') {
      FIELD_GROUP_BACKGROUND_COLOR = palette.white;
      FIELD_COLOR = palette.black;
      FIELD_COLOR_PLACEHOLDER_NOT_DISABLED = palette.neutralDark;
      FIELD_COLOR_PLACEHOLDER_DISABLED = palette.neutralPrimary;
      ERROR_MESSAGE_COLOR = DarkColors.appRed;
    }

    // Contrast theme overrides
    if (themeName === 'contrast') {
      FIELD_GROUP_BACKGROUND_COLOR = palette.white;
      FIELD_GROUP_OPACITY = 1;
      FIELD_COLOR = palette.black;
      FIELD_COLOR_PLACEHOLDER_NOT_DISABLED = palette.black;
      FIELD_COLOR_PLACEHOLDER_DISABLED = ContrastColors.appGreenContrast;
      FIELD_GROUP_BORDER_WIDTH = '1px 1px 1px 1px';
      FIELD_GROUP_BORDER_COLOR = palette.black;
      FIELD_GROUP_BORDER_COLOR_DISABLED = ContrastColors.appGreenContrast;
      FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE = palette.themeDark;
      FIELD_GROUP_BORDER_WIDTH_ACTIVE = '1px 1px 2px 1px';
      ERROR_MESSAGE_COLOR = palette.black;
    }

    // If this textfiled is on gray background
    if (background === 'gray') {
      FIELD_GROUP_BACKGROUND_COLOR = palette.white;
      FIELD_COLOR_PLACEHOLDER_NOT_DISABLED = palette.neutralPrimaryAlt;
    }

    const isActiveOpenStyles = {
      selectors: {
        '.ms-TextField-fieldGroup': {
          borderBottomColor: FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE,
          borderWidth: FIELD_GROUP_BORDER_WIDTH_ACTIVE,
          // Need 2px border raidus as the border width is 2px.
          borderBottomRightRadius: teamsButtonBorderRadius,
          borderBottomLeftRadius: teamsButtonBorderRadius
        },
        textarea: {
          selectors: {
            '& .ms-TextField-fieldGroup': {
              height: 'auto'
            }
          }
        }
      }
    };

    const styles = {
      root: {
        selectors: {
          '.ms-TextField-fieldGroup': {
            background: FIELD_GROUP_BACKGROUND_COLOR
          },
          '&.is-active': { ...isActiveOpenStyles },
          '.ms-DatePicker.is-open &': { ...isActiveOpenStyles },
          '&.is-disabled': {
            selectors: {
              '.ms-TextField-fieldGroup': {
                borderColor: FIELD_GROUP_BORDER_COLOR_DISABLED,
                selectors: {
                  'input, textarea': {
                    color: FIELD_COLOR_PLACEHOLDER_DISABLED
                  }
                }
              }
            }
          }
        }
      },
      subComponentStyles: {
        label: getLabelStyles
      },
      fieldGroup: [
        {
          borderRadius: teamsBorderRadius,
          borderWidth: FIELD_GROUP_BORDER_WIDTH,
          borderStyle: 'Solid',
          borderColor: FIELD_GROUP_BORDER_COLOR,
          height: !multiline ? FIELD_HEIGHT : 'auto'
        },
        disabled && {
          opacity: FIELD_GROUP_OPACITY
        },
        !focused &&
          !disabled &&
          !hasErrorMessage && {
            selectors: {
              ':hover': {
                borderColor: FIELD_GROUP_BORDER_COLOR
              }
            }
          },
        hasErrorMessage && [
          {
            borderColor: FIELD_GROUP_BORDER_COLOR,
            color: 'red',
            selectors: {
              '&:focus, &:hover': {
                borderColor: FIELD_GROUP_BORDER_COLOR
              }
            }
          }
        ],
        focused && {
          selectors: {
            '::after': {
              border: 'none'
            }
          }
        }
      ],
      field: {
        color: FIELD_COLOR,
        height: !multiline ? FIELD_HEIGHT : MULTILINE_FIELD_HEIGHT,
        padding: !multiline ? '0 12px 2px' : '7px 12px 9px',
        selectors: {
          '::placeholder': [
            !disabled && { color: FIELD_COLOR_PLACEHOLDER_NOT_DISABLED },
            disabled && { color: FIELD_COLOR_PLACEHOLDER_DISABLED }
          ],
          ':-ms-input-placeholder': [
            !disabled && { color: FIELD_COLOR_PLACEHOLDER_NOT_DISABLED },
            disabled && { color: FIELD_COLOR_PLACEHOLDER_DISABLED }
          ]
        }
      },
      errorMessage: {
        color: ERROR_MESSAGE_COLOR
      }
    };
    return styles;
  };
}
